import PropTypes from "prop-types"

import React, { useState, useEffect, useMemo } from "react"
import TextField from "@mui/material/TextField"
import InputAdornment from "@mui/material/InputAdornment"
import SearchIcon from "@mui/icons-material/Search"
import ChapterAccordion from "./ChapterAccordion"
import {
  Box,
  Stack,
  Grid,
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Checkbox,
} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"

const ProjectsColumn = ({
  projectsHeaders = [],
  onProjectExpand,
  projectsData = [],
  loadingProjectId,
  selectedItems = {},
  onSelect,
  initialExpandedProjectId,
  organizationSelected,
  setOrganizationSelected,
}) => {
  const [expanded, setExpanded] = useState(initialExpandedProjectId || false)
  const [search, setSearch] = useState("")
  useEffect(() => {
    if (initialExpandedProjectId) {
      setExpanded(initialExpandedProjectId)
    }
  }, [initialExpandedProjectId])
  const handleChange = projectId => (event, isExpanded) => {
    setExpanded(isExpanded ? projectId : false)
    if (isExpanded && onProjectExpand) {
      onProjectExpand(projectId)
    }
  }

  const getProjectData = projectId =>
    projectsData.find(p => p.projectId === projectId)
  const handleOrganizationCheckbox = e => {
    setOrganizationSelected(e.target.checked)
  }

  const hasSelectedItems = projectId => {
    const projectData = getProjectData(projectId)
    if (!projectData?.chapters) return false

    const checkChapterRecursive = chapter => {
      if (selectedItems[chapter.chapterId]) return true
      if (chapter.partidas?.some(p => selectedItems[p.partidaId])) return true
      if (chapter.chapters?.some(checkChapterRecursive)) return true
      return false
    }

    return projectData.chapters.some(checkChapterRecursive)
  }

  const projectsToDisplay = useMemo(() => {
    if (!Array.isArray(projectsHeaders) || projectsHeaders.length === 0)
      return []
    const term = search.trim().toLowerCase()
    return projectsHeaders
      .map(project => {
        if (!term) return project

        if (project.name.toLowerCase().includes(term)) return project

        const projectData = projectsData.find(
          p => p.projectId === project.projectId,
        )
        if (!projectData || !Array.isArray(projectData.chapters)) return null

        function filterPartidasRecursive(partida) {
          if (partida.name && partida.name.toLowerCase().includes(term)) {
            return { ...partida }
          }
          return null
        }
        function filterChaptersRecursive(chapter) {
          if (chapter.name && chapter.name.toLowerCase().includes(term)) {
            return { ...chapter }
          }
          let filteredChildren = []
          if (Array.isArray(chapter.chapters)) {
            filteredChildren = chapter.chapters
              .map(filterChaptersRecursive)
              .filter(Boolean)
          }
          let filteredPartidas = []
          if (Array.isArray(chapter.partidas)) {
            filteredPartidas = chapter.partidas
              .map(filterPartidasRecursive)
              .filter(Boolean)
          }
          if (filteredChildren.length > 0 || filteredPartidas.length > 0) {
            return {
              ...chapter,
              chapters: filteredChildren,
              partidas: filteredPartidas,
            }
          }
          return null
        }

        const filteredChapters = projectData.chapters
          .map(filterChaptersRecursive)
          .filter(Boolean)
        if (filteredChapters.length > 0) {
          return {
            ...project,
            filteredChapters,
          }
        }
        return null
      })
      .filter(Boolean)
  }, [projectsHeaders, projectsData, search])
  return (
    <Grid item xs={12} md={4}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 1,
          mt: 2,
        }}
      >
        <Typography
          sx={{
            fontWeight: "medium",
            flexShrink: 0,
          }}
        >
          {projectsHeaders.length === 1 ? "Capítulos y partidas" : "Proyectos"}
        </Typography>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Buscar..."
          sx={{ ml: 2, flex: 1, minWidth: 0 }}
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          height: "463px",
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {(projectsHeaders.length === 0 || projectsHeaders.length > 1) && (
          <Card
            sx={{
              mb: 2,
              backgroundColor: "grey.300",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "grey.400",
              },
            }}
          >
            <CardContent
              sx={{ py: 1.5, px: 2, display: "flex", alignItems: "center" }}
            >
              <Checkbox
                sx={{ p: 0, mr: 1 }}
                checked={!!organizationSelected}
                onChange={handleOrganizationCheckbox}
              />
              <Typography sx={{ fontWeight: "bold" }}>Organización</Typography>
            </CardContent>
          </Card>
        )}
        <Box sx={{ flex: 1, overflowY: "auto" }}>
          {projectsToDisplay.map((project, idx) => {
            const onlyOne = projectsHeaders.length === 1
            return (
              <Accordion
                key={project.projectId}
                expanded={expanded === project.projectId}
                onChange={handleChange(project.projectId)}
                sx={{
                  mb: 0.5,
                  "&:before": {
                    display: "none",
                  },
                  boxShadow: "none",
                  border: onlyOne ? "none" : "1px solid",
                  borderColor: onlyOne ? "transparent" : "divider",
                }}
              >
                {!onlyOne && (
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`panel-${project.projectId}-content`}
                    id={`panel-${project.projectId}-header`}
                    sx={{
                      backgroundColor: hasSelectedItems(project.projectId)
                        ? "#b8daf6"
                        : "grey.300",
                      "&:hover": {
                        backgroundColor: hasSelectedItems(project.projectId)
                          ? "#a0cef0"
                          : "grey.400",
                      },
                      "& .MuiAccordionSummary-expandIconWrapper": {
                        marginLeft: 1,
                        marginRight: 0,
                      },
                      "& .MuiAccordionSummary-content": {
                        margin: "6px 0 !important",
                        minWidth: 0,
                        flex: 1,
                      },
                      minHeight: "40px !important",
                      height: "40px",
                      "&.Mui-expanded": {
                        minHeight: "40px !important",
                        height: "40px",
                        marginBottom: "5px",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: "medium",
                          fontSize: "0.9rem",
                          flex: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {project.name}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                )}
                <AccordionDetails sx={{ p: 0.5, pt: 0 }}>
                  {expanded === project.projectId &&
                    (loadingProjectId === project.projectId ? (
                      <Stack
                        sx={{
                          height: 100,
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        cargando capitulos...
                      </Stack>
                    ) : (
                      (() => {
                        // Si hay filteredChapters, mostrar solo esos; si no, mostrar todos
                        const chaptersToShow =
                          project.filteredChapters ||
                          getProjectData(project.projectId)?.chapters ||
                          []
                        return chaptersToShow.length > 0 ? (
                          chaptersToShow.map(chapter => (
                            <ChapterAccordion
                              key={chapter.chapterId}
                              chapter={chapter}
                              selectedItems={selectedItems}
                              onSelect={onSelect}
                            />
                          ))
                        ) : (
                          <span style={{ color: "#888" }}>
                            No hay resultados
                          </span>
                        )
                      })()
                    ))}
                </AccordionDetails>
              </Accordion>
            )
          })}
        </Box>
      </Paper>
    </Grid>
  )
}

export default ProjectsColumn

ProjectsColumn.propTypes = {
  projectsHeaders: PropTypes.arrayOf(
    PropTypes.shape({
      projectId: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onProjectExpand: PropTypes.func,
  projectsData: PropTypes.array,
  loadingProjectId: PropTypes.string,
  selectedItems: PropTypes.object,
  onSelect: PropTypes.func,
  initialExpandedProjectId: PropTypes.string,
  organizationSelected: PropTypes.bool,
  setOrganizationSelected: PropTypes.func,
}
